import React, {useEffect, useState} from "react";
import Files from 'react-files';
import {Label} from "react-foundation";
import {toast} from "react-toastify";
import VersionSelect from "./Components/VersionSelect";
import UploadTemplateHelp from "./Components/UploadTemplateHelp";
import CanonicalStoreOverridesPreviewModal from "./Components/CanonicalStoreOverridesPreviewModal";
import {uploadTemplates} from "./uploadTemplates";
import ReactTable from "../Components/ReactTable";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import {makeApiCall} from "../../Api/makeApiCall";
import {toastError} from "../../utils/toast";
import {faIcons} from "../../utils/iconConstants";
import {
    canonicalStoreOverridesBulkUploadHistoryUrl,
    previewCanonicalStoreOverridesUpload,
} from "./canonicalStoreOverrides";

const TEXT_COLUMN = {filterFn: 'includesString'};

const historyColumns = [
    {header: 'UPLOADED AT', accessorKey: 'timestamp', size: 200, ...TEXT_COLUMN},
    {header: 'USER', accessorKey: 'user', size: 220, ...TEXT_COLUMN},
    {header: 'VERSION', accessorKey: 'version', ...TEXT_COLUMN},
    {header: 'FILE', accessorKey: 'filename', size: 250, ...TEXT_COLUMN},
    {
        header: 'STORED FILE',
        accessorKey: 'gcs_path',
        enableColumnFilter: false,
        cell: ({getValue}) => getValue()
            ? <div className="open-json-payload-modal">
                <a className="button tertiary"
                   href={getValue().replace("gs://", "https://storage.cloud.google.com/")}
                   target="_blank"
                   rel="noreferrer">View CSV</a>
            </div>
            : null
    },
    {header: 'ROWS APPLIED', accessorKey: 'rows_applied'},
    {header: 'ROWS SKIPPED', accessorKey: 'rows_skipped'},
    {header: 'STATUS', accessorKey: 'status', ...TEXT_COLUMN},
];

const readFileText = (file) => new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.onload = (loadedFile) => resolve(loadedFile.target.result);
    fileReader.onerror = () => reject(fileReader.error);
    fileReader.readAsText(file);
});

const ProjectionCanonicalStoreOverridesUpload = () => {
    const [previewing, setPreviewing] = useState(false);
    const [preview, setPreview] = useState(null);
    const [version, setVersion] = useState("");
    const [history, setHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(undefined);
    const [historyRefreshTrigger, setHistoryRefreshTrigger] = useState(undefined);
    const selectedVersion = version.trim();

    useEffect(() => {
        let isCurrent = true;
        setHistoryLoading(true);
        setHistoryError(undefined);
        makeApiCall(canonicalStoreOverridesBulkUploadHistoryUrl())
            .then(response => {
                if (!isCurrent) return;
                if (response?.apiError) {
                    setHistory([]);
                    setHistoryError('Failed to load upload history');
                } else {
                    setHistory(response?.data ?? []);
                }
            })
            .finally(() => isCurrent && setHistoryLoading(false));
        return () => {
            isCurrent = false;
        };
    }, [historyRefreshTrigger]);

    const previewFile = (file, contents) =>
        previewCanonicalStoreOverridesUpload(selectedVersion, file.name, contents)
            .then(result => setPreview({version: selectedVersion, filename: file.name, contents, result}))
            .catch(error => toastError(toast, error.message));

    const canUpload = !!selectedVersion && !previewing;

    const onFilesChange = (files) => {
        if (!canUpload || !files.length) return;
        const file = files[0];
        setPreviewing(true);
        return readFileText(file)
            .then(contents => previewFile(file, contents))
            .catch(error => toastError(toast, `Unable to read ${file.name}: ${error.message}`))
            .finally(() => setPreviewing(false));
    };

    const onFileError = (error) =>
        toastError(toast, `Failed to upload file. Error Code: ${error.code} Message: ${error.message}`);

    const onUploaded = () => {
        setPreview(null);
        setHistoryRefreshTrigger(Date.now());
    };

    return (
        <>
            <div className={'projection-upload-trigger-dag-page-controls store-overrides-upload-controls'}>
                <div className={'projection-upload-select-container'}>
                    <VersionSelect version={version} setVersion={setVersion} restrictToAvailableVersions isDisabled={previewing}/>
                </div>
                <div className={'upload-button-container'}>
                    <Label className={"csv-label"} title={"Upload Projections"}>
                        <Files
                            className={`hollow button secondary files-dropzone ${canUpload ? '' : 'disabled'}`}
                            onChange={onFilesChange}
                            onError={onFileError}
                            accepts={['.csv']}
                            multiple
                            maxFiles={1}
                            maxFileSize={20000000}
                            clickable={canUpload}
                        >
                            {previewing
                                ? <><i className={`${faIcons.spinner} fa-spin`} data-testid={"spinner"}/>
                                    <span>Verifying CSV</span></>
                                : 'Upload Overrides'}
                        </Files>
                    </Label>
                </div>
                <UploadTemplateHelp template={uploadTemplates.canonicalStoreOverrides}/>
            </div>
            <div className="sld-dag-airflowlogs-container" data-testid="store-overrides-history-container">
                <h5>Upload History</h5>
                {historyError && <div className={'callout alert small'} data-testid={'store-overrides-history-error'}>{historyError}</div>}
                <LoadingWrapper loading={historyLoading}>
                    <ReactTable className={'store-overrides-history-table'}
                                filterable
                                data={history}
                                columns={historyColumns}
                                initialState={{pagination: {pageSize: 10}}}
                    />
                </LoadingWrapper>
            </div>
            {preview &&
                <CanonicalStoreOverridesPreviewModal version={preview.version}
                                                     filename={preview.filename}
                                                     contents={preview.contents}
                                                     preview={preview.result}
                                                     onClose={() => setPreview(null)}
                                                     onUploaded={onUploaded}
                />}
        </>
    );
};

export default ProjectionCanonicalStoreOverridesUpload;
